/* eslint-disable no-nested-ternary */
/* eslint-disable promise/always-return */
import React, { ElementType, useEffect, useMemo, useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import * as Icons from 'react-pay-icons';
import braintree from 'braintree-web';
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Typography,
  Radio,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  Paper,
  RadioGroup,
  Checkbox,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import {
  selectIsLoggedIn,
  selectUserRole,
  selectUser,
} from 'renderer/features/auth/state/userSelectors';
import { useGetCartQuery } from 'renderer/features/payments-page/services/cart.endpoints';
import { useListPaymentMethodsQuery } from 'renderer/features/settings/services/paymentMethods.endpoints';
import { useAppSelector } from 'renderer/shared/state/hooks';
import AccountForm from 'renderer/features/settings/components/AccountForm';
import BillingAddressForm from 'renderer/features/settings/components/BillingAddressForm';
import { useGetProductDetailsQuery } from 'renderer/features/products-page/services/products.endpoints';
import {
  formatCurrency,
  formatQueryError,
} from 'renderer/shared/formatters/formatters';
import { Form, Formik } from 'formik';
import { ZodSchema } from 'zod';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { confirmStripePayment } from 'renderer/shared/payments/confirmStripePayment';
import {
  authenticationMessageForRequiresAction,
  resolveCheckoutLoaderMessage,
} from 'renderer/shared/payments/checkoutLoaderMessages';
import { cartItemKey } from 'renderer/features/payments-page/cart/cartItemKey';
import type { SerialRowMap } from 'renderer/features/payments-page/cart/cartTypes';
import { computeCartTotals } from 'renderer/features/payments-page/cart/cartTotals';
import PayPalLogo from './paypal-logo.png';
import CreditCardLogos from './credit-card-logos.png'; // combine Visa, MasterCard, Amex into a single image
import OrderSummary from './OrderSummary';
import DiscountCode from './DiscountCode';
import MainCard from './MainCard';

import DAlogo from './da-logo.png';
import DAlogo2 from './da-logo2.png';
import DAlogo3 from './da-logo3.png';

import {
  LoggedInUserExistingCardSchema,
  LoggedInUserNewCardSchema,
  LoggedInUserPayPalSchema,
  PublicUserNewAccountPayPalSchema,
  PublicUserNewAccountSchema,
  PublicUserNoAccountPayPalSchema,
  PublicUserNoAccountSchema,
  PurchaseForm,
} from '../schemas/purchase.schemas';
import type {
  PurchaseResponse,
  RequiresActionResponse,
} from '../schemas/purchase.schemas';
import {
  useGetConfigQuery,
  useMakePurchaseMutation,
  useFinalizePurchaseMutation,
  useCreatePaypalCheckoutMutation,
} from '../services/purchase.endpoints';
import PurchaseConfirmationPage from './PurchaseConfirmationPage';
import StripeCreditCardForm from './StripeCreditCardForm';
import ExistingPaymentMethods from './ExistingPaymentMethods';
import PaymentMethodForm from './PaymentMethodForm';
import FullScreenLoader from './FullScreenLoader';
import PlainFullScreenLoader from './PlainFullScreenLoader';
import PaymentHeader from './PaymentHeader';
import { isPurchaseConfirmation } from '../utils/applyCheckoutAuth';
import {
  buildPaypalCancelUrl,
  buildPaypalReturnUrl,
} from '../utils/paypalRedirectUrls';
import {
  AUTO_RENEWAL_DISCLOSURE_VERSION,
  buildAutoRenewalDisclosureLines,
} from '../utils/autoRenewalDisclosure';

const isRequiresActionResponse = (
  result: PurchaseResponse | RequiresActionResponse,
): result is RequiresActionResponse =>
  typeof result === 'object' &&
  result !== null &&
  'type' in result &&
  result.type === 'requires_action';

async function completePurchaseAfterRequiresAction(
  result: PurchaseResponse | RequiresActionResponse,
  deps: {
    stripe: ReturnType<typeof useStripe>;
    finalizePurchase: ReturnType<typeof useFinalizePurchaseMutation>[0];
    setAuthenticationMessage: (message: string) => void;
    createAccount: boolean;
    email: string;
  },
): Promise<PurchaseResponse> {
  if (!isRequiresActionResponse(result)) {
    return result;
  }

  const {
    stripe,
    finalizePurchase,
    setAuthenticationMessage,
    createAccount,
    email,
  } = deps;
  if (!stripe) {
    throw new Error('Stripe is not initialized');
  }

  setAuthenticationMessage(
    authenticationMessageForRequiresAction(result.requiresAuthentication),
  );
  await confirmStripePayment(stripe, result.clientSecret);
  setAuthenticationMessage('Finalizing your order…');

  const nextResult = await finalizePurchase({
    checkoutSessionId: result.checkoutSessionId,
    paymentIntentId: result.paymentIntentId,
    transactionIds: result.transactionIds,
    createAccount,
    email,
  }).unwrap();

  return completePurchaseAfterRequiresAction(nextResult, deps);
}

// map card types to icons
const CARD_ICONS: Record<string, ElementType> = {
  visa: Icons.Visa,
  mastercard: Icons.Mastercard,
  // 'master card': Icons.Mastercard,
  amex: Icons.Amex,
  // 'american express': Icons.Amex,
  discover: Icons.Discover,
  // diners: Icons.Diners,
  // jcb: Icons.Jcb,
  // unionpay: Icons.UnionPay,
  // maestro: Icons.Maestro,
};

// import "./styles.css";

const initialForm: PurchaseForm = {
  nameOnCard: '',
  email: '',
  firstName: '',
  lastName: '',
  streetAddress: '',
  locality: '',
  region: '',
  regionCode: '',
  postalCode: '',
  countryName: '',
  countryCode: '',
  password: '',
  confirmPassword: '',
  createAccount: false,
  savePayment: false,
  makeDefault: false,
  discountCode: '',
  // hostApplication: {
  //   id: '',
  //   version: '',
  //   displayName: '',
  // },
  // platform: '',
  isNewPaymentMethod: true,
  newPaymentMethodToken: '',
  numberValidity: '',
  cvvValidity: '',
  expirationDateValidity: '',
  existingPaymentMethodToken: '',
  // numberOfCopies: 1,
  selectedPlan: '',
  paymentProvider: 'card', // 'card' | 'paypal'
  autoRenewalConsent: false,
} as const;

const resolvePurchaseHostApplication = (item: any, serialRows: any[] = []) => {
  // Non-upgrades behave as before
  if (item.productId?.category !== 'Upgrade') {
    return item.hostApplication ?? null;
  }

  // Prefer All-Hosts row (priceOverride is the invariant)
  const allHostsRow = serialRows.find((r) => {
    const app = r.targetHostApp || r.hostApp;
    if (!app) return false;

    const name = String(app.displayName || '').toLowerCase();
    return (
      name.includes('all host') ||
      (typeof app.priceOverride === 'number' &&
        !Number.isNaN(app.priceOverride) &&
        app.priceOverride !== 0)
    );
  });

  if (allHostsRow) {
    return allHostsRow.targetHostApp || allHostsRow.hostApp;
  }

  // Otherwise first valid host app
  const firstHostRow = serialRows.find((r) => {
    const app = r.targetHostApp || r.hostApp;
    return !!app?.id;
  });

  return firstHostRow?.targetHostApp || firstHostRow?.hostApp || null;
};

type PurchaseLocationState = {
  paypalCaptureError?: string;
  paypalCancelled?: boolean;
  paymentProvider?: 'card' | 'paypal';
};

function Purchase() {
  const location = useLocation();
  const purchaseReturnState = location.state as PurchaseLocationState | null;
  const [paypalReturnError] = useState(
    () => purchaseReturnState?.paypalCaptureError ?? null,
  );
  const [paypalCancelled] = useState(
    () => purchaseReturnState?.paypalCancelled ?? false,
  );
  const [returnedPaymentProvider] = useState(
    () => purchaseReturnState?.paymentProvider ?? null,
  );

  // const [createPaypalCheckout] = useCreatePaypalCheckoutMutation();
  const [
    createPaypalCheckout,
    {
      error: paypalError, // grab error from PayPal mutation
      isLoading: paypalLoading,
      isSuccess: paypalSuccess,
    },
  ] = useCreatePaypalCheckoutMutation();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [completedPurchase, setCompletedPurchase] =
    useState<PurchaseResponse | null>(null);
  const [authenticationMessage, setAuthenticationMessage] = useState('');
  const [showCheckoutLoader, setShowCheckoutLoader] = useState(false);

  const role = useAppSelector(selectUserRole);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (paypalReturnError || paypalCancelled || returnedPaymentProvider) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    location.pathname,
    navigate,
    paypalCancelled,
    paypalReturnError,
    returnedPaymentProvider,
  ]);

  // Stripe hooks
  const stripe = useStripe();
  const elements = useElements();

  const { id: productId } = useParams();
  const { data: productDetails, error: productError } =
    useGetProductDetailsQuery(productId ?? '');
  console.log('productDetails', productDetails);
  const { data: config, error: configError } = useGetConfigQuery();
  const [makePurchase, { error: purchaseError }] = useMakePurchaseMutation();
  const [finalizePurchase] = useFinalizePurchaseMutation();
  console.log('purchaseError', purchaseError);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const [hostedFieldsInstance, setHostedFieldsInstance] =
    useState<braintree.HostedFields | null>(null);
  const user = useAppSelector(selectUser);

  // const { data: cartData } = useGetCartQuery();
  const { data: cartData, refetch: refetchCart } = useGetCartQuery(null, {
    // Another tab may have checked out or replaced this cart; never price a
    // cached copy from before this page mounted.
    refetchOnMountOrArgChange: true,
  });
  console.log('cartData', cartData);

  // Buy Now amount must match Cart / OrderSummary (copy caps + choose-min).
  const items = useMemo(() => cartData?.items || [], [cartData?.items]);
  const serialRowsByItem: SerialRowMap = useMemo(() => {
    const map: SerialRowMap = {};
    items.forEach((it: any) => {
      map[cartItemKey(it)] = it.serialMap || [];
    });
    return map;
  }, [items]);
  const { grandTotal: total } = useMemo(
    () => computeCartTotals(items, serialRowsByItem),
    [items, serialRowsByItem],
  );

  const hasSubscription = cartData?.items?.some(
    (item: any) => item.productId?.category === 'Subscription',
  );
  const hasUpgrade = cartData?.items?.some(
    (item: any) => item.productId?.category === 'Upgrade',
  );
  console.log('hasSubscription?', hasSubscription);

  const provider = cartData?.items?.some(
    (item: any) => item.productId?.category === 'Subscription',
  )
    ? 'stripe'
    : 'braintree';
  console.log('provider', provider);

  console.log('user', user);

  const category = provider === 'stripe' ? 'Subscription' : 'Video Plugin';
  const { data: paymentMethods, isFetching: isPaymentMethodsFetching } =
    useListPaymentMethodsQuery(category, { skip: !isLoggedIn });

  const { data: stripePaymentMethods } = useListPaymentMethodsQuery('stripe');
  const { data: braintreePaymentMethods } =
    useListPaymentMethodsQuery('braintree');
  // 🔁 Redirect logic - TEMPORARY FIX FOR CUSTOMERS GOING TO OLD FF2 LINK
  const restrictedProductId = '66341fef4895dadba053975d'; // FF2
  const newProductId = '67f6b5c241f8c9345e025d6d'; // FF3
  // useEffect(() => {
  //   if (
  //     (role === 'user' || role === 'public') &&
  //     productId === restrictedProductId
  //   ) {
  //     navigate(`/purchase/${newProductId}`, { replace: true });
  //   }
  // }, [role, productId, navigate]);

  // If the user is not logged in and making a public purchase for a subscription,
  // create an account and save the payment method
  // useEffect(() => {
  //   if (productDetails?.category === 'Subscription' && !isLoggedIn) {
  //     // setFormData((prev) => ({
  //     //   ...prev,
  //     //   createAccount: true,
  //     //   savePayment: true,
  //     // }));
  //   }
  // }, [productDetails, isLoggedIn]);

  // if (productError) {
  //   return <FullScreenLoader message={formatQueryError(productError)} />;
  // }
  if (configError) {
    return <FullScreenLoader message={formatQueryError(configError)} />;
  }
  // if (!productDetails || !config) {
  //   return <FullScreenLoader message="Loading..." />;
  // }

  const braintreeClientToken = config?.braintreeClientToken;

  if (completedPurchase) {
    return <PurchaseConfirmationPage {...completedPurchase} />;
  }

  const handleSubmit = async (
    values: typeof initialForm,
    actions: { setFieldError: (arg0: string, arg1: string) => void },
  ) => {
    setHasSubmitted(true);
    if (isSubmitting) return;

    if (hasSubscription && !values.autoRenewalConsent) {
      actions.setFieldError(
        'autoRenewalConsent',
        'Please agree to the automatic renewal terms to complete your purchase.',
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Branch: PayPal vs Card
      // if (values.paymentProvider === 'paypal') {
      //   console.log('🟦 PayPal flow starting...');

      //   const {
      //     createAccount,
      //     firstName,
      //     lastName,
      //     email,
      //     password,
      //     confirmPassword,
      //   } = values;

      //   // Build init request
      //   const paypalInitRequest = {
      //     user: {
      //       id: '_id' in user ? user._id : undefined,
      //       create: createAccount,
      //       firstName,
      //       lastName,
      //       email,
      //       password,
      //       confirmPassword,
      //     },
      //     purchases: cartData?.items.map((item: any) => ({
      //       productId: item.productId._id || item.productId,
      //       category: item.productId.category,
      //       numberOfCopies: item.quantity,
      //       hostApplication: item.hostApplication,
      //       discountCode: item.discountId?.code || null,
      //     })),
      //     cartId: cartData?._id,
      //     returnUrl: `${window.location.origin}/#/purchase/confirm?cartId=${cartData?._id}`,
      //     cancelUrl: `${window.location.origin}/purchase/cancel`,
      //   };

      //   console.log('➡️ Sending PayPal init request', paypalInitRequest);

      //   // Call backend to create PayPal checkout order
      //   const result = await createPaypalCheckout(paypalInitRequest).unwrap();
      //   console.log('✅ PayPal checkout created', result);

      //   if (result?.approvalUrl && result?.id) {
      //     // Store order/cart mapping for capture step
      //     localStorage.setItem(
      //       'paypalApproval',
      //       JSON.stringify({
      //         orderId: result.id,
      //         cartId: cartData?._id,
      //         user: {
      //           id: '_id' in user ? user._id : undefined,
      //           create: createAccount,
      //           firstName,
      //           lastName,
      //           email,
      //           password,
      //           confirmPassword,
      //         },
      //       }),
      //     );

      //     // Redirect to PayPal approval page
      //     window.location.href = result.approvalUrl;
      //     return; // stop further processing
      //   }

      //   throw new Error('❌ No PayPal approvalUrl returned from backend');
      // }

      if (values.paymentProvider === 'paypal') {
        console.log('🟦 PayPal flow starting...');

        const {
          createAccount,
          firstName,
          lastName,
          email,
          password,
          confirmPassword,
        } = values;

        // Build init request
        const paypalInitRequest = {
          user: {
            id: '_id' in user ? user._id : undefined,
            create: createAccount,
            firstName,
            lastName,
            email,
            password,
            confirmPassword,
          },
          purchases: cartData?.items.map((item: any) => ({
            productId: item.productId._id || item.productId,
            category: item.productId.category,
            numberOfCopies: item.quantity,
            hostApplication: resolvePurchaseHostApplication(
              item,
              item.serialMap || [],
            ),

            discountCode: item.discountId?.code || null,
          })),
          cartId: cartData?._id,
          returnUrl: buildPaypalReturnUrl(cartData?._id),
          cancelUrl: buildPaypalCancelUrl(cartData?._id),
        };

        console.log('➡️ Sending PayPal init request', paypalInitRequest);

        // ✅ Do NOT unwrap – this way RTKQ manages `purchaseError`
        const result = await createPaypalCheckout(paypalInitRequest);

        if ('data' in result && result.data) {
          const { id, approvalUrl } = result.data;

          if (approvalUrl && id) {
            localStorage.setItem(
              'paypalApproval',
              JSON.stringify({
                orderId: id,
                cartId: cartData?._id,
                user: {
                  id: '_id' in user ? user._id : undefined,
                  create: createAccount,
                  firstName,
                  lastName,
                  email,
                  password,
                  confirmPassword,
                },
              }),
            );

            window.location.href = approvalUrl;
            return; // ✅ success redirect
          }

          // ❌ response didn’t contain approvalUrl or id
          setIsSubmitting(false);
          return;
        }

        //  If we get here, it failed → reset spinner
        setIsSubmitting(false);
        return;
      }

      // 🔶 Existing Stripe/Braintree flow for cards
      let newPaymentMethodToken = '';
      const cardProvider = cartData?.items?.some(
        (item: any) => item.productId?.category === 'Subscription',
      )
        ? 'stripe'
        : 'braintree';

      const {
        createAccount,
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
        savePayment,
        existingPaymentMethodToken,
        isNewPaymentMethod,
        nameOnCard,
        streetAddress,
        locality,
        region,
        postalCode,
        countryName,
        countryCode,
        makeDefault,
      } = values;

      if (cardProvider === 'stripe' && isNewPaymentMethod === true) {
        if (!stripe || !elements) {
          actions.setFieldError(
            'newPaymentMethodToken',
            'Payment form is still loading. Please try again.',
          );
          setIsSubmitting(false);
          return;
        }
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
          actions.setFieldError(
            'newPaymentMethodToken',
            'Card details are unavailable. Please refresh and try again.',
          );
          setIsSubmitting(false);
          return;
        }
        const payload = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: nameOnCard,
            address: {
              line1: streetAddress,
              city: locality,
              state: region,
              postal_code: postalCode,
              country: countryCode || undefined,
            },
          },
        });
        if (payload.error) {
          actions.setFieldError(
            'newPaymentMethodToken',
            payload.error.message ||
              'Could not tokenize card details. Please check your card and try again.',
          );
          setIsSubmitting(false);
          return;
        }
        newPaymentMethodToken = payload.paymentMethod?.id || '';
        if (!newPaymentMethodToken) {
          actions.setFieldError(
            'newPaymentMethodToken',
            'Could not tokenize card details. Please try again.',
          );
          setIsSubmitting(false);
          return;
        }
      } else if (hostedFieldsInstance && isNewPaymentMethod) {
        try {
          const { nonce } = await hostedFieldsInstance.tokenize();
          newPaymentMethodToken = nonce;
        } catch (error) {
          actions.setFieldError(
            'newPaymentMethodToken',
            'Encountered an error while tokenizing card details. Please try again.',
          );
          setIsSubmitting(false);
          return;
        }
      }

      if (isNewPaymentMethod) {
        if (!newPaymentMethodToken) {
          actions.setFieldError(
            'newPaymentMethodToken',
            'Please enter valid card details before completing checkout.',
          );
          setIsSubmitting(false);
          return;
        }
      } else if (!existingPaymentMethodToken) {
        actions.setFieldError(
          'existingPaymentMethodToken',
          'Please choose a saved payment method.',
        );
        setIsSubmitting(false);
        return;
      }

      const request = {
        user: {
          id: '_id' in user ? user._id : undefined,
          create: createAccount,
          firstName,
          lastName,
          email,
          password,
          confirmPassword,
        },
        paymentMethod: {
          provider: cardProvider,
          id: isNewPaymentMethod ? undefined : existingPaymentMethodToken,
          create: isNewPaymentMethod ? savePayment : undefined,
          makeDefault: isNewPaymentMethod ? makeDefault : undefined,
          // makeDefault, // ✅ always include, regardless of new or existing
          token: isNewPaymentMethod ? newPaymentMethodToken : undefined,
          billingAddress: isNewPaymentMethod
            ? {
                nameOnCard,
                streetAddress,
                locality,
                region,
                postalCode,
                countryName,
              }
            : undefined,
        },
        cartId: cartData?._id,
        purchases: cartData?.items.map((item: any, index: number) => ({
          productId: item.productId._id || item.productId,
          numberOfCopies: item.quantity,
          platform: item.platform,
          hostApplication: resolvePurchaseHostApplication(
            item,
            item.serialMap || [],
          ),
          serialMap: item.serialMap,
          serial: item.serial,
          isFinal: index === cartData.items.length - 1,
          discountCode: item.discountId?.code,
        })),
        autoRenewalConsent: hasSubscription
          ? {
              agreed: values.autoRenewalConsent,
              agreedAt: new Date().toISOString(),
              disclosureVersion: AUTO_RENEWAL_DISCLOSURE_VERSION,
              disclosureText: buildAutoRenewalDisclosureLines(
                cartData?.items || [],
              ).join(' '),
            }
          : undefined,
      };

      setShowCheckoutLoader(true);
      const checkoutResult = await completePurchaseAfterRequiresAction(
        await makePurchase(request).unwrap(),
        {
          stripe,
          finalizePurchase,
          setAuthenticationMessage,
          createAccount,
          email,
        },
      );

      if (isPurchaseConfirmation(checkoutResult)) {
        setCompletedPurchase(checkoutResult);
      }

      refetchCart();
    } catch (err) {
      console.error('Purchase failed', err);
    } finally {
      setAuthenticationMessage('');
      setShowCheckoutLoader(false);
      setIsSubmitting(false);
    }
  };

  const hasSavedCards =
    isLoggedIn && paymentMethods && paymentMethods?.length > 0;
  const defaultCard = hasSavedCards
    ? paymentMethods.find((pm) => pm.default) || paymentMethods[0]
    : null;

  const hasCheckoutError = Boolean(
    purchaseError || paypalError || paypalReturnError,
  );

  const isWaitingForPaymentMethods =
    isLoggedIn &&
    isPaymentMethodsFetching &&
    paymentMethods === undefined &&
    !hasCheckoutError &&
    !completedPurchase &&
    !showCheckoutLoader &&
    !isSubmitting;

  if (isWaitingForPaymentMethods) {
    return <PlainFullScreenLoader message="" />;
  }

  const shouldShowStripeAlert =
    category === 'Video Plugin' &&
    paymentMethods?.length === 0 &&
    (stripePaymentMethods?.length ?? 0) > 0;

  const shouldShowBraintreeAlert =
    category === 'Subscription' &&
    paymentMethods?.length === 0 &&
    (braintreePaymentMethods?.length ?? 0) > 0;

  // Buy Now total is computed via computeCartTotals above (matches OrderSummary).

  return (
    <Formik
      initialValues={{
        ...initialForm,
        paymentProvider: returnedPaymentProvider ?? initialForm.paymentProvider,
        createAccount: !isLoggedIn,
        // savePayment: true,
        isNewPaymentMethod: !hasSavedCards,
        existingPaymentMethodToken: defaultCard?._id ?? '',
      }}
      validate={(values) => {
        console.log('VALIDATE');
        // --- PayPal Flows ---
        if (values.paymentProvider === 'paypal') {
          if (!isLoggedIn) {
            if (values.createAccount) {
              console.log(1);
              return toValidator(PublicUserNewAccountPayPalSchema)(values);
            }
            console.log(2);
            return toValidator(PublicUserNoAccountPayPalSchema)(values);
          }
          console.log(3);
          console.log('values', values);
          console.log(
            'validation result',
            toValidator(LoggedInUserPayPalSchema)(values),
          );
          return toValidator(LoggedInUserPayPalSchema)(values);
        }

        // --- Card Flows ---
        let validationSchema: ZodSchema = PublicUserNoAccountSchema;

        if (!isLoggedIn) {
          if (values.createAccount) {
            console.log(4);
            validationSchema = PublicUserNewAccountSchema;
          } else {
            console.log(5);
            validationSchema = PublicUserNoAccountSchema;
          }
        } else if (values.isNewPaymentMethod) {
          console.log(6);
          validationSchema = LoggedInUserNewCardSchema;
        } else {
          console.log(7);
          validationSchema = LoggedInUserExistingCardSchema;
        }
        console.log(8);
        return toValidator(validationSchema)(values);
      }}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors }) => {
        if (showCheckoutLoader && hasSubmitted) {
          return (
            <FullScreenLoader
              message={resolveCheckoutLoaderMessage(authenticationMessage)}
            >
              <Box textAlign="center">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  This may take a few moments. Do not close or refresh the page.
                </Typography>
              </Box>
            </FullScreenLoader>
          );
        }

        return (
          <Form>
            <Box
              component="main"
              sx={{
                flexGrow: 1,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ px: { xs: 0, md: 5 }, mb: 2 }}
              >
                {/* Left side: Title + Secure checkout text */}
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ mb: 0.75, textAlign: { xs: 'center', md: 'left' } }}
                  >
                    Complete Your Purchase
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}
                  >
                    <LockIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Secure checkout – all transactions are encrypted
                    </Typography>
                  </Stack>
                </Box>

                {/* Right side: Card logos */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{
                    justifyContent: { xs: 'center', md: 'flex-end' },
                    mt: { xs: 2, md: 0 },
                  }}
                >
                  {Object.entries(CARD_ICONS).map(([key, Icon]) => (
                    <Icon key={key} style={{ width: 50, height: 'auto' }} />
                  ))}
                  <img src={PayPalLogo} alt="PayPal" style={{ height: 40 }} />
                </Stack>
              </Stack>

              <Grid container sx={{ p: 0, mt: 5 }}>
                <Grid item xs={12} md={7} sx={{ px: { xs: 0, sm: 0, md: 5 } }}>
                  <MainCard
                    border={false}
                    key={values.paymentProvider}
                    title="Payment"
                    contentSX={{ px: 0 }}
                  >
                    {/* Card re-entry notice only applies to card payments, not PayPal */}
                    {values.paymentProvider === 'card' &&
                      (shouldShowStripeAlert || shouldShowBraintreeAlert) && (
                        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                          For security reasons, we need you to re-enter your
                          credit card details before completing this purchase.
                        </Alert>
                      )}
                    {paypalCancelled && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        PayPal checkout was cancelled. You can try again or
                        choose another payment method.
                      </Alert>
                    )}
                    {(purchaseError || paypalError || paypalReturnError) && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {paypalReturnError ||
                          formatQueryError(purchaseError || paypalError)}
                      </Alert>
                    )}

                    <br />
                    <PaymentHeader
                      hasSavedCards={!!hasSavedCards}
                      paymentProvider={
                        values.paymentProvider as 'card' | 'paypal'
                      }
                      isNewPaymentMethod={values.isNewPaymentMethod}
                      setFieldValue={setFieldValue}
                      hasSubscription={hasSubscription}
                      hasUpgrade={hasUpgrade}
                      defaultPaymentMethodId={defaultCard?._id}
                    />

                    {/* Radio selector: Existing, New, PayPal */}
                    {/* <RadioGroup
                      sx={{ mt: 1 }}
                      value={
                        values.paymentProvider === 'paypal'
                          ? 'paypal'
                          : values.isNewPaymentMethod
                          ? 'new'
                          : 'saved'
                      }
                      onChange={(_, v) => {
                        if (v === 'paypal') {
                          setFieldValue('paymentProvider', 'paypal');
                        } else {
                          setFieldValue('paymentProvider', 'card');
                          setFieldValue('isNewPaymentMethod', v === 'new');
                        }
                      }}
                    >
                      {hasSavedCards && (
                        <FormControlLabel
                          value="saved"
                          control={<Radio />}
                          label={
                            <Typography variant="subtitle1" fontWeight="bold">
                              Existing Credit Cards
                            </Typography>
                          }
                        />
                      )}
                      <FormControlLabel
                        value="new"
                        control={<Radio />}
                        label={
                          <Typography variant="subtitle1" fontWeight="bold">
                            New Credit Card
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="paypal"
                        control={<Radio />}
                        label={
                          <Typography variant="subtitle1" fontWeight="bold">
                            PayPal
                          </Typography>
                        }
                      />
                    </RadioGroup> */}

                    {/* Existing card option */}
                    {values.paymentProvider === 'card' &&
                      !values.isNewPaymentMethod &&
                      hasSavedCards && (
                        <Box sx={{ mt: 2 }}>
                          <ExistingPaymentMethods
                            paymentMethods={paymentMethods}
                            category={
                              provider === 'stripe'
                                ? 'Subscription'
                                : 'Video Plugin'
                            }
                          />
                        </Box>
                      )}

                    {/* New card option */}
                    {values.paymentProvider === 'card' &&
                      values.isNewPaymentMethod && (
                        <Box sx={{ mt: 2 }}>
                          {provider === 'stripe' ? (
                            <StripeCreditCardForm />
                          ) : (
                            <PaymentMethodForm
                              disabled={false}
                              clientToken={braintreeClientToken ?? ''}
                              hostedFieldsInstance={hostedFieldsInstance}
                              setHostedFieldsInstance={setHostedFieldsInstance}
                            />
                          )}
                        </Box>
                      )}

                    {/* {values.paymentProvider === 'paypal' && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Pay securely with PayPal
                        </Typography>
                        <PayPalScriptProvider
                          options={{
                            clientId: config?.paypalClientId ?? '', // comes from your backend /config
                            currency: 'USD',
                            intent: 'CAPTURE',
                          }}
                        >
                          <PayPalButtons
                            style={{
                              layout: 'vertical',
                              shape: 'rect',
                              color: 'gold',
                            }}
                            createOrder={async () => {
                              // Call your backend to create a PayPal order
                              const res = await fetch('/api/paypal/checkout', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  cartId: cartData?._id,
                                  purchases: cartData?.items,
                                  returnUrl: `${window.location.origin}/purchase/confirm`,
                                  cancelUrl: `${window.location.origin}/purchase/cancel`,
                                }),
                              });
                              const data = await res.json();
                              console.log('PayPal order created:', data);
                              return data.id; // PayPal order ID
                            }}
                            onApprove={async (data) => {
                              console.log('PayPal approved:', data);
                              // Call backend to capture
                              const res = await fetch(
                                `/api/paypal/capture/${data.orderID}`,
                                {
                                  method: 'POST',
                                },
                              );
                              const confirmationResult = await res.json();
                              console.log(
                                'PayPal capture result:',
                                confirmationResult,
                              );

                              // Navigate to your confirmation page
                              navigate('/purchase/confirm', {
                                state: confirmationResult,
                              });
                            }}
                            onError={(err) => {
                              console.error('PayPal error:', err);
                            }}
                          />
                        </PayPalScriptProvider>
                      </Box>
                    )} */}

                    {/* Billing + Account info — always shown no matter what */}
                    {/* <Typography variant="h6" sx={{ my: 2 }}>
                      Billing Address
                    </Typography>
                    <BillingAddressForm disabled={false} /> */}
                    {values.paymentProvider === 'card' &&
                      values.isNewPaymentMethod && (
                        <>
                          <Typography variant="h6" sx={{ my: 2 }}>
                            Billing Address
                          </Typography>
                          <BillingAddressForm disabled={false} />
                        </>
                      )}
                    <AccountForm
                      disabled={false}
                      hasSubscription={hasSubscription}
                      paymentProvider={
                        values.paymentProvider as 'card' | 'paypal'
                      }
                    />
                  </MainCard>

                  {/* Action buttons pinned bottom of left column */}
                  {/* <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                      fullWidth
                      sx={{
                        backgroundColor: '#1E88E5',
                        fontWeight: 'bold',
                        color: '#fff',
                        py: 1.5,
                        mb: 1.5,
                        '&:hover': { backgroundColor: '#1565C0' },
                      }}
                    >
                      {isSubmitting ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          justifyContent="center"
                        >
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                          <span>Processing…</span>
                        </Stack>
                      ) : (
                        `BUY NOW – $${cartData?.total ?? '...'}`
                      )}
                    </Button>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.8rem', mb: 1 }}
                    >
                      Secure checkout • Instant license • 30-day guarantee
                    </Typography>

                    <Link to="/" style={{ textDecoration: 'none' }}>
                      <Button
                        variant="text"
                        color="primary"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Cancel
                      </Button>
                    </Link>
                  </Box> */}

                  <Box sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
                    {hasSubscription && (
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, mb: 2, textAlign: 'left' }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          Automatic Renewal Terms
                        </Typography>
                        {buildAutoRenewalDisclosureLines(
                          cartData?.items || [],
                        ).map((line) => (
                          <Typography
                            key={line}
                            variant="body2"
                            sx={{ mb: 0.5 }}
                          >
                            {line}
                          </Typography>
                        ))}
                        <FormControlLabel
                          sx={{ mt: 1 }}
                          control={
                            <Checkbox
                              checked={values.autoRenewalConsent}
                              onChange={(e) =>
                                setFieldValue(
                                  'autoRenewalConsent',
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Typography variant="body2">
                              I agree that my subscription will automatically
                              renew as described above until I cancel.
                            </Typography>
                          }
                        />
                        {typeof errors.autoRenewalConsent === 'string' &&
                          !values.autoRenewalConsent && (
                            <Typography variant="body2" color="error">
                              {errors.autoRenewalConsent}
                            </Typography>
                          )}
                      </Paper>
                    )}
                    {/* <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                      fullWidth
                      sx={{
                        backgroundColor: '#1E88E5',
                        fontWeight: 'bold',
                        color: '#fff',
                        py: 1.5,
                        mb: 1.5,
                        '&:hover': { backgroundColor: '#1565C0' },
                      }}
                    >
                      {isSubmitting ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          justifyContent="center"
                        >
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                          <span>Processing…</span>
                        </Stack>
                      ) : (
                        `BUY NOW – ${formatCurrency(total)}`
                      )}
                    </Button> */}
                    <Button
                      variant="contained"
                      color="secondary"
                      type="submit"
                      disabled={isSubmitting}
                      fullWidth
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.3rem',
                        lineHeight: 1,
                        py: 1.75,
                        mb: 1.5,
                      }}
                    >
                      {isSubmitting ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          justifyContent="center"
                        >
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                          <span>Processing…</span>
                        </Stack>
                      ) : (
                        `Buy Now – ${formatCurrency(total)}`
                      )}
                    </Button>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        // fontSize: '0.9rem',
                        mb: 1,
                      }}
                    >
                      Secure checkout • Instant license • Encrypted payments
                    </Typography>

                    <Link to="/" style={{ textDecoration: 'none' }}>
                      <Button
                        variant="text"
                        color="primary"
                        sx={{
                          fontWeight: 600,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Cancel
                      </Button>
                    </Link>
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <MainCard
                    border={false}
                    title="Order Summary"
                    sx={{ borderRadius: '4px 4px 0 0' }}
                    content={false}
                  >
                    <OrderSummary
                      // productDetails={productDetails}
                      cartData={cartData}
                      // selectedPlan={values.selectedPlan}
                      // onSelectPlan={(plan: string) =>
                      //   setFieldValue('selectedPlan', plan)
                      // }
                    />
                  </MainCard>

                  <MainCard
                    // title="Order Summary"
                    sx={{ borderRadius: '4px 4px 0 0', p: 2, mt: 2 }}
                    content={false}
                  >
                    <DiscountCode
                      initialCode={
                        cartData?.items?.find(
                          (item: any) => item.discountId?.code,
                        )?.discountId.code || ''
                      }
                      onDiscountUpdate={() => refetchCart()}
                    />
                  </MainCard>

                  {/* <Box
                    sx={
                      {
                        // display: 'flex',
                        // justifyContent: 'flex-end',
                        // gap: 2,
                        // mt: 3,
                      }
                    }
                  >
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                      fullWidth
                      sx={{
                        my: 2,
                        mr: 3,
                        backgroundColor: '#1E88E5',
                        fontWeight: 'bold',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#1565C0',
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                      ) : (
                        'Purchase'
                      )}
                    </Button>

                    <Link to="/">
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          fontWeight: 'bold',
                          // textTransform: 'none',
                          // mt: 2,
                        }}
                      >
                        Cancel
                      </Button>
                    </Link>
                  </Box> */}

                  <div
                    style={{
                      // backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '20px',
                      // boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <p>
                      <b>Trouble Checking Out?</b>
                    </p>
                    <p>
                      You can also purchase at{' '}
                      <a
                        href="https://store.digitalanarchy.com"
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        store.digitalanarchy.com
                      </a>{' '}
                      or contact us.
                    </p>
                    <p>
                      📞 <b>415-287-6069</b>, 10am–5pm PST
                    </p>
                    <p>
                      <b>Digital Downloads</b>
                    </p>
                    <p>
                      All products/invoices are emailed to you. Check spam!
                      Don&apos;t see it? Email <b>sales@digitalanarchy.com</b>.
                    </p>
                  </div>
                </Grid>
              </Grid>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

export default Purchase;
